import { Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiClassificationStatusEnum } from '../../../enums/AiClassificationStatusEnum';
import { useTags } from '../../../hooks/useTags';
import type File from '../../../interfaces/File';
import { FileService } from '../../../services/fileApiService';
import { Icon } from '../../Core/Icon';
import type { iconComponents } from '../../Core/IconImports';

interface FileClassificationStatusIconProps {
  file: File;
}

const fileService = new FileService();

const FileAiClassificationStatusIcon = ({ file }: FileClassificationStatusIconProps) => {
  const { t } = useTranslation();
  const defaultTooltipContent =
    file.aiOperationStatus === AiClassificationStatusEnum.Error
      ? file.aiOperationErrorMessage || t('notifications.tags.error')
      : file.aiOperationStatus === AiClassificationStatusEnum.Queued
        ? t('aiClassificationStatus.queued')
        : file.aiOperationStatus === AiClassificationStatusEnum.Classifying
          ? t('common.classificationInProgress')
          : t('common.clickForDetailAiClassification');
  const [tooltipContent, setTooltipContent] = useState(defaultTooltipContent);
  const [loading, setLoading] = useState(false);
  const { tags } = useTags();

  useEffect(() => {
    setTooltipContent(defaultTooltipContent);
  }, [defaultTooltipContent]);

  /**
   * Handles the click event for the classification status icon.
   * If the file is already classified, it will show the tags.
   * If the file is not classified, it will show the classification status.
   */
  const handleClick = async () => {
    if (
      loading ||
      file.aiOperationStatus === AiClassificationStatusEnum.Queued ||
      file.aiOperationStatus === AiClassificationStatusEnum.Classifying ||
      file.aiOperationStatus === AiClassificationStatusEnum.Error ||
      tooltipContent !== t('common.clickForDetailAiClassification')
    )
      return;

    setLoading(true);
    try {
      const response = await fileService.getFile(file.id);

      // if response is empty, set tooltip content to "No tags found"
      if (response.tags.length === 0) {
        setTooltipContent(t('errors.noFileClassificationAvailable'));
        return;
      }

      const tagsWithExplanation = response.tags.filter((tag) => tag.explanation);
      if (tagsWithExplanation.length === 0) {
        setTooltipContent(t('common.noTagsApplied'));
        return;
      }

      setTooltipContent(
        tagsWithExplanation
          .map((tag) => {
            const t = tags.find((t) => t.id === tag.id);
            return `- ${t?.title}: ${tag.explanation}`;
          })
          .join('\n'),
      );
    } catch (error) {
      setTooltipContent(
        t('errors.genericFetchError', { resource: t('common.aiClassificationStatus') }),
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Returns the icon for the classification status.
   * @param status - The classification status.
   * @returns The icon for the classification status.
   */
  const getIconForStatus = (status: AiClassificationStatusEnum): keyof typeof iconComponents => {
    switch (status) {
      case AiClassificationStatusEnum.Queued:
        return 'CalendarClockIcon';
      case AiClassificationStatusEnum.Classifying:
        return 'LoaderIcon';
      case AiClassificationStatusEnum.Done:
        return 'TagAICheckIcon';
      case AiClassificationStatusEnum.Error:
        return 'TagAiTriangleAlertIcon';
      default:
        return 'TagAiLoaderIcon';
    }
  };

  const iconClassName =
    file.aiOperationStatus === AiClassificationStatusEnum.Classifying
      ? 'text-zinc h-5 w-5 animate-spin'
      : 'text-zinc h-5 w-5';

  return (
    <Tooltip title={loading ? t('common.loading') : tooltipContent}>
      <div onClick={handleClick} className="cursor-pointer">
        <Icon name={getIconForStatus(file.aiOperationStatus)} className={iconClassName} />
      </div>
    </Tooltip>
  );
};

export default FileAiClassificationStatusIcon;
